import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import RichTextEditor from '../components/RichTextEditor';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    status: 'draft',
    meta_title: '',
    meta_description: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    const fetchPostsData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?status=all`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPosts(data.posts || []);
        } else if (response.status === 401) {
          navigate('/admin/login');
        } else {
          setError('Failed to fetch blog posts');
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
        setError('Network error. Please check if the backend server is running.');
      } finally {
        setLoading(false);
      }
    };

    fetchPostsData();
  }, [navigate]);

  const fetchPosts = async () => {
    // This function is now inlined in useEffect but kept for refresh button
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?status=all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        setError('Failed to fetch blog posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Network error. Please check if the backend server is running.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-generate slug from title
    if (name === 'title' && !editingPost) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('alt_text', formData.title);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          featured_image: `${process.env.REACT_APP_BACKEND_URL}${data.url}`
        }));
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const method = editingPost ? 'PUT' : 'POST';
      const url = editingPost 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${editingPost.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowEditor(false);
        setEditingPost(null);
        resetForm();
        fetchPosts();
        alert(editingPost ? 'Post updated successfully!' : 'Post created successfully!');
      } else {
        alert('Failed to save post');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      status: post.status,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      tags: post.tags || []
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featured_image: '',
      status: 'draft',
      meta_title: '',
      meta_description: '',
      tags: []
    });
    setTagInput('');
  };

  const handleNewPost = () => {
    resetForm();
    setEditingPost(null);
    setShowEditor(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-secondary',
      published: 'bg-success',
      archived: 'bg-warning'
    };
    return badges[status] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading blog management...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Blog Management - WeFlare Admin"
        description="Manage WeFlare blog posts and content"
        robots="noindex, nofollow"
      />
      
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
          <div className="container-fluid">
            <span className="navbar-brand">
              <i className="bi bi-journal-text me-2"></i>
              Blog Management
            </span>
            
            <div className="navbar-nav ms-auto">
              <button className="btn btn-light me-3" onClick={handleNewPost}>
                <i className="bi bi-plus-circle me-2"></i>
                New Post
              </button>
              <button className="btn btn-outline-light" onClick={() => navigate('/admin/dashboard')}>
                <i className="bi bi-arrow-left me-2"></i>
                Back to Dashboard
              </button>
            </div>
          </div>
        </nav>

        <div className="container-fluid py-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {!showEditor ? (
            /* Posts List */
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-list-ul me-2"></i>
                  Blog Posts
                </h5>
                <button className="btn btn-primary" onClick={handleNewPost}>
                  <i className="bi bi-plus-circle me-2"></i>
                  New Post
                </button>
              </div>
              <div className="card-body">
                {posts.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-journal-x fs-1 text-muted mb-3"></i>
                    <p className="text-muted">No blog posts found</p>
                    <button className="btn btn-primary" onClick={handleNewPost}>
                      Create Your First Post
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Status</th>
                          <th>Author</th>
                          <th>Published</th>
                          <th>Views</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.map((post) => (
                          <tr key={post.id}>
                            <td>
                              <div>
                                <strong>{post.title}</strong>
                                {post.excerpt && (
                                  <div className="text-muted small">
                                    {post.excerpt.substring(0, 100)}...
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadge(post.status)}`}>
                                {post.status.toUpperCase()}
                              </span>
                            </td>
                            <td>{post.author_name || 'Unknown'}</td>
                            <td>{post.published_at ? formatDate(post.published_at) : '-'}</td>
                            <td>{post.view_count || 0}</td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button 
                                  className="btn btn-outline-primary"
                                  onClick={() => handleEdit(post)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <a 
                                  href={`/blog/${post.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-secondary"
                                >
                                  <i className="bi bi-eye"></i>
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Post Editor */
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-pencil me-2"></i>
                  {editingPost ? 'Edit Post' : 'New Post'}
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-8">
                      {/* Title */}
                      <div className="mb-3">
                        <label className="form-label">Title *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {/* Slug */}
                      <div className="mb-3">
                        <label className="form-label">Slug *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="slug"
                          value={formData.slug}
                          onChange={handleInputChange}
                          required
                        />
                        <small className="text-muted">URL-friendly version of the title</small>
                      </div>

                      {/* Content */}
                      <div className="mb-3">
                        <label className="form-label">Content *</label>
                        <RichTextEditor
                          value={formData.content}
                          onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                          placeholder="Write your blog post content here. Use the toolbar above to format text, add images, create lists, and more..."
                        />
                        <small className="text-muted">
                          Use the rich text editor above to format your content. You can add headings, bold text, images, links, lists, and more.
                        </small>
                      </div>

                      {/* Excerpt */}
                      <div className="mb-3">
                        <label className="form-label">Excerpt</label>
                        <textarea
                          className="form-control"
                          name="excerpt"
                          value={formData.excerpt}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Brief summary of the post (optional)"
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      {/* Status */}
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>

                      {/* Featured Image */}
                      <div className="mb-3">
                        <label className="form-label">Featured Image</label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                        {formData.featured_image && (
                          <div className="mt-2">
                            <img
                              src={formData.featured_image}
                              alt="Featured"
                              className="img-thumbnail"
                              style={{ maxWidth: '200px' }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Tags */}
                      <div className="mb-3">
                        <label className="form-label">Tags</label>
                        <div className="input-group mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="Add a tag"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleTagAdd();
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleTagAdd}
                          >
                            Add
                          </button>
                        </div>
                        <div>
                          {formData.tags.map((tag, index) => (
                            <span key={index} className="badge bg-primary me-1 mb-1">
                              {tag}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.7rem' }}
                                onClick={() => handleTagRemove(tag)}
                              ></button>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* SEO Meta */}
                      <div className="mb-3">
                        <label className="form-label">Meta Title</label>
                        <input
                          type="text"
                          className="form-control"
                          name="meta_title"
                          value={formData.meta_title}
                          onChange={handleInputChange}
                          placeholder="SEO title (optional)"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Meta Description</label>
                        <textarea
                          className="form-control"
                          name="meta_description"
                          value={formData.meta_description}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="SEO description (optional)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowEditor(false)}
                    >
                      Cancel
                    </button>
                    <div>
                      <button
                        type="submit"
                        className="btn btn-primary me-2"
                        onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                      >
                        Save as Draft
                      </button>
                      <button
                        type="submit"
                        className="btn btn-success"
                        onClick={() => setFormData(prev => ({ ...prev, status: 'published' }))}
                      >
                        {editingPost ? 'Update & Publish' : 'Publish'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminBlog;